import { useState, useEffect } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "../utils/stripeConfig";
import { AddCardForm } from "../components/AddCardForm";
import { Button as UiButton } from "@shadcn-in-nx/ui/button";
import { Card } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Wallet, CreditCard, Building2, Link2, DollarSign, ShoppingBag } from "lucide-react";
import { Bluelagoon } from "@bluelagoon";

// Get icon component based on payment method type
function getPaymentMethodIcon(pm) {
    const type = pm.type || pm.icon;

    switch (type) {
        case "card":
            return <CreditCard className="w-6 h-6" />;
        case "us_bank_account":
        case "sepa_debit":
        case "acss_debit":
        case "bacs_debit":
        case "au_becs_debit":
        case "bank":
        case "ideal":
            return <Building2 className="w-6 h-6" />;
        case "link":
            return <Link2 className="w-6 h-6" />;
        case "cashapp":
        case "paypal":
        case "revolut_pay":
            return <DollarSign className="w-6 h-6" />;
        case "klarna":
        case "affirm":
        case "afterpay_clearpay":
            return <ShoppingBag className="w-6 h-6" />;
        default:
            return <Wallet className="w-6 h-6" />;
    }
}

// Get background color based on payment method type
function getPaymentMethodColor(pm) {
    const type = pm.type || pm.icon;

    switch (type) {
        case "card":
            return "from-blue-500 to-blue-600";
        case "us_bank_account":
        case "sepa_debit":
        case "acss_debit":
        case "bacs_debit":
        case "au_becs_debit":
        case "bank":
        case "ideal":
            return "from-green-500 to-green-600";
        case "link":
            return "from-teal-500 to-teal-600";
        case "cashapp":
            return "from-emerald-500 to-emerald-600";
        case "paypal":
            return "from-indigo-500 to-indigo-600";
        case "klarna":
            return "from-pink-500 to-pink-600";
        case "affirm":
            return "from-blue-600 to-blue-700";
        case "amazon_pay":
            return "from-orange-500 to-orange-600";
        default:
            return "from-gray-500 to-gray-600";
    }
}

// Get subtitle text based on payment method type
function getPaymentMethodSubtitle(pm, t) {
    switch (pm.type) {
        case "card":
            if (pm.expMonth && pm.expYear) {
                return `${t?.("billing.expires") || "Expires"} ${pm.expMonth}/${pm.expYear}`;
            }
            return null;
        case "us_bank_account":
            return pm.accountType ? `${pm.accountType} account` : "Bank account";
        case "sepa_debit":
            return pm.country ? `SEPA (${pm.country})` : "SEPA Direct Debit";
        case "link":
            return "Stripe Link";
        case "cashapp":
            return "Cash App";
        case "paypal":
            return "PayPal";
        case "amazon_pay":
            return "Amazon Pay";
        case "klarna":
            return "Buy now, pay later";
        case "affirm":
            return "Buy now, pay later";
        case "afterpay_clearpay":
            return "Buy now, pay later";
        default:
            return pm.type?.replace(/_/g, " ");
    }
}

export function PaymentMethodsTab({
    paymentMethods,
    paymentMethodsLoading,
    showAddCardModal,
    setShowAddCardModal,
    onRemovePaymentMethod,
    onSetDefaultPaymentMethod,
    removingPaymentMethod,
    settingDefaultPaymentMethod,
    user,
    addPaymentMethod,
    refreshPaymentMethods,
    t,
}) {
    const [clientSecret, setClientSecret] = useState(null);
    const [setupIntentLoading, setSetupIntentLoading] = useState(false);
    const [setupIntentError, setSetupIntentError] = useState(null);

    // Fetch SetupIntent clientSecret when modal opens
    useEffect(() => {
        let isCancelled = false;

        if (showAddCardModal && user?.id && !clientSecret) {
            setSetupIntentLoading(true);
            setSetupIntentError(null);

            Bluelagoon.createSetupIntent({ userId: user.id })
                .then((response) => {
                    if (isCancelled) return;
                    const clientSecretValue =
                        response?.data?.clientSecret ||
                        response?.data?.data?.clientSecret ||
                        response?.clientSecret;

                    if (clientSecretValue) {
                        setClientSecret(clientSecretValue);
                    } else {
                        console.error("Setup intent response:", response);
                        setSetupIntentError(response?.error || response?.data?.error || "Failed to initialize payment");
                    }
                })
                .catch((error) => {
                    if (isCancelled) return;
                    console.error("Error creating setup intent:", error);
                    setSetupIntentError(error.message || "Failed to initialize payment");
                })
                .finally(() => {
                    if (!isCancelled) {
                        setSetupIntentLoading(false);
                    }
                });
        }

        // Reset when modal closes
        if (!showAddCardModal) {
            setClientSecret(null);
            setSetupIntentError(null);
        }

        return () => {
            isCancelled = true;
        };
    }, [showAddCardModal, user?.id, clientSecret]);

    // Elements options for PaymentElement
    const elementsOptions = clientSecret ? {
        clientSecret,
        appearance: {
            theme: 'stripe',
            variables: {
                colorPrimary: '#2563eb',
                colorBackground: '#ffffff',
                colorText: '#1f2937',
                colorDanger: '#ef4444',
                fontFamily: 'system-ui, sans-serif',
                spacingUnit: '4px',
                borderRadius: '8px',
            },
        },
    } : undefined;

    return (
        <div className="max-w-4xl mx-auto">
            {/* Add Card Modal */}
            {showAddCardModal && (
                <div
                    className="fixed top-0 mt-0 left-0 right-0 bottom-0 inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 safe-area-inset billing-modal-overlay"
                    style={{
                        marginTop: "env(safe-area-inset-top)",
                        position: "fixed",
                        zIndex: 9999,
                    }}
                    onClick={(e) => {
                        // Close modal when clicking backdrop
                        if (e.target === e.currentTarget) {
                            setShowAddCardModal(false);
                        }
                    }}
                >
                    <div
                        className="bg-white rounded-2xl max-w-lg w-full p-8 max-h-[90vh] overflow-y-auto overscroll-contain billing-modal-content"
                        style={{
                            maxHeight: "min(90vh, 90dvh)",
                            position: "relative",
                            zIndex: 10000,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-start mb-6">
                            <h2 className="text-2xl font-bold">
                                {t("billing.addPaymentMethod") || "Add Payment Method"}
                            </h2>
                            <button
                                onClick={() => setShowAddCardModal(false)}
                                className="text-gray-400 hover:text-gray-600 text-2xl leading-none min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
                                aria-label={t("common.close") || "Close"}
                            >
                                ×
                            </button>
                        </div>

                        {setupIntentLoading && (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                                <p className="text-gray-600">{t("billing.initializingPayment") || "Initializing payment..."}</p>
                            </div>
                        )}

                        {setupIntentError && (
                            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                                <p>{setupIntentError}</p>
                                <button
                                    onClick={() => {
                                        setClientSecret(null);
                                        setSetupIntentError(null);
                                    }}
                                    className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
                                >
                                    {t("common.tryAgain") || "Try again"}
                                </button>
                            </div>
                        )}

                        {!setupIntentLoading && !setupIntentError && clientSecret && (
                            <Elements stripe={stripePromise} options={elementsOptions}>
                                <AddCardForm
                                    onSuccess={() => {
                                        setShowAddCardModal(false);
                                        refreshPaymentMethods();
                                    }}
                                    onCancel={() => setShowAddCardModal(false)}
                                    userId={user?.id}
                                    addPaymentMethod={addPaymentMethod}
                                    t={t}
                                    clientSecret={clientSecret}
                                />
                            </Elements>
                        )}
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold">
                        {t("billing.paymentMethods") || "Payment Methods"}
                    </h2>
                    <p className="text-gray-600 text-sm mt-1">
                        {t("billing.managePaymentMethods") ||
                            "Manage your saved payment methods"}
                    </p>
                </div>
                <UiButton
                    onClick={() => setShowAddCardModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                    <svg
                        className="w-4 h-4 mr-2 inline"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                        />
                    </svg>
                    {t("billing.addPaymentMethod") || "Add Payment Method"}
                </UiButton>
            </div>

            {/* Payment Methods List */}
            {paymentMethodsLoading ? (
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    <p className="mt-2 text-gray-600">
                        {t("billing.loadingPaymentMethods") ||
                            "Loading payment methods..."}
                    </p>
                </div>
            ) : paymentMethods.length === 0 ? (
                <Card className="p-8 text-center">
                    <Wallet className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">
                        {t("billing.noPaymentMethods") || "No payment methods"}
                    </h3>
                    <p className="text-gray-600 mb-4">
                        {t("billing.addFirstPaymentMethod") ||
                            "Add your first payment method to get started"}
                    </p>
                    <UiButton
                        onClick={() => setShowAddCardModal(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        {t("billing.addPaymentMethod") || "Add Payment Method"}
                    </UiButton>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {paymentMethods.map((pm) => (
                        <Card key={pm.id} className="p-6 relative">
                            {pm.isDefault && (
                                <Badge className="absolute top-4 right-4 bg-green-100 text-green-800 border-green-300">
                                    {t("billing.default") || "Default"}
                                </Badge>
                            )}
                            <div className="flex items-start gap-4">
                                <div className={`w-12 h-12 bg-gradient-to-br ${getPaymentMethodColor(pm)} rounded-lg flex items-center justify-center text-white`}>
                                    {getPaymentMethodIcon(pm)}
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="font-semibold text-lg capitalize">
                                            {pm.displayName || `${pm.brand} •••• ${pm.last4}`}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        {getPaymentMethodSubtitle(pm, t)}
                                    </p>
                                    <div className="flex gap-2 mt-4">
                                        {!pm.isDefault && (
                                            <UiButton
                                                onClick={() =>
                                                    onSetDefaultPaymentMethod(
                                                        pm.id
                                                    )
                                                }
                                                disabled={
                                                    settingDefaultPaymentMethod
                                                }
                                                variant="outline"
                                                size="sm"
                                                className="text-xs"
                                            >
                                                {t("billing.setAsDefault") ||
                                                    "Set as Default"}
                                            </UiButton>
                                        )}
                                        <UiButton
                                            onClick={() =>
                                                onRemovePaymentMethod(pm.id)
                                            }
                                            disabled={
                                                removingPaymentMethod ||
                                                pm.isDefault
                                            }
                                            variant="outline"
                                            size="sm"
                                            className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                            title={
                                                pm.isDefault
                                                    ? t("billing.cannotRemoveDefault") || "Cannot remove default payment method"
                                                    : t("billing.removePaymentMethod") || "Remove payment method"
                                            }
                                        >
                                            {t("billing.remove") || "Remove"}
                                        </UiButton>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
